import { useTranslation } from 'react-i18next';
import './FilterSidebar.css';

export default function FilterSidebarComponent({ filterState, setFilterState }) {
  const { t } = useTranslation();

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setFilterState(prev => ({
      ...prev,
      search: value,
    }));
  };

  const clearSearch = () => {
    setFilterState(prev => ({
      ...prev,
      search: '',
    }));
  };

  const toggleFilterValue = (key, value) => {
    setFilterState(prev => {
      const exists = prev[key].includes(value);
      return {
        ...prev,
        [key]: exists
          ? prev[key].filter(v => v !== value)
          : [...prev[key], value],
      };
    });
  };

  const hasActiveFilters =
    (filterState.search && filterState.search.trim() !== '') ||
    (filterState.type && filterState.type !== 'all') ||
    (filterState.sizes && filterState.sizes.length > 0) ||
    (filterState.genders && filterState.genders.length > 0) ||
    (filterState.statuses && filterState.statuses.length > 0);

  const resetAllFilters = () => {
    setFilterState({
      type: 'all',
      sizes: [],
      genders: [],
      statuses: [],
      search: '',
    });
  };

  return (
    <div className="filter-sidebar-container">
      {/* TYPE FILTER */}
      <div className="card filter-card">
        <h5 className="filter-card-title">
          <i className="fas fa-filter"></i> {t('adoptionPage.filter_type')}
        </h5>
        <div className="type-segmented-control">
          {[
            { id: 'all', iconClass: 'fas fa-paw' },
            { id: 'dog', iconClass: 'flaticon-dog-8' },
            { id: 'cat', iconClass: 'flaticon-cat' }
          ].map(({ id, iconClass }) => (
            <button
              key={id}
              type="button"
              className={`type-pill-btn ${filterState.type === id ? 'active' : ''}`}
              onClick={() => setFilterState(f => ({ ...f, type: id }))}
            >
              <i className={iconClass}></i>
              {t(`adoptionPage.filter_${id}${id === 'all' ? '' : 's'}`)}
            </button>
          ))}
        </div>
      </div>

      {/* SIZE FILTER */}
      <div className="card filter-card">
        <h5 className="filter-card-title">
          <i className="fas fa-expand-arrows-alt"></i> {t('adoptionPage.size')}
        </h5>
        <div className="chips-container">
          {['small', 'medium', 'large'].map(size => {
            const isChecked = filterState.sizes.includes(size);
            return (
              <div key={size} className="position-relative">
                <input
                  type="checkbox"
                  id={`size-chip-${size}`}
                  className="visually-hidden"
                  checked={isChecked}
                  onChange={() => toggleFilterValue('sizes', size)}
                />
                <label htmlFor={`size-chip-${size}`} className="filter-chip-label">
                  <i className={`fas ${isChecked ? 'fa-check-circle' : 'fa-circle'} filter-chip-icon`}></i>
                  {t(`adoptionPage.filter_${size}`)}
                </label>
              </div>
            );
          })}
        </div>
      </div>

      {/* GENDER FILTER */}
      <div className="card filter-card">
        <h5 className="filter-card-title">
          <i className="fas fa-venus-mars"></i> {t('adoptionPage.gender')}
        </h5>
        <div className="chips-container">
          {['male', 'female'].map(gender => {
            const isChecked = filterState.genders.includes(gender);
            const genderIcon = gender === 'male' ? 'fa-mars' : 'fa-venus';
            return (
              <div key={gender} className="position-relative">
                <input
                  type="checkbox"
                  id={`gender-chip-${gender}`}
                  className="visually-hidden"
                  checked={isChecked}
                  onChange={() => toggleFilterValue('genders', gender)}
                />
                <label htmlFor={`gender-chip-${gender}`} className="filter-chip-label">
                  <i className={`fas ${isChecked ? 'fa-check-circle' : genderIcon} filter-chip-icon`}></i>
                  {t(`common.gender.${gender}`)}
                </label>
              </div>
            );
          })}
        </div>
      </div>


      {/* STATUS FILTER */}
      <div className="card filter-card">
        <h5 className="filter-card-title">
          <i className="fas fa-info-circle"></i> {t('adoptionPage.location')}
        </h5>
        <div className="chips-container">
          {[
            { value: 'available', labelKey: 'adoptionPage.filter_available_bih' },
            { value: 'fosterDK', labelKey: 'adoptionPage.filter_available_denmark' },
            { value: 'fosterCZ', labelKey: 'adoptionPage.filter_available_czech_republic' },
          ].map(statusOption => {
            const isChecked = filterState.statuses?.includes(statusOption.value);
            return (
              <div key={statusOption.value} className="position-relative">
                <input
                  type="checkbox"
                  id={`status-chip-${statusOption.value}`}
                  className="visually-hidden"
                  checked={isChecked}
                  onChange={() => toggleFilterValue('statuses', statusOption.value)}
                />
                <label htmlFor={`status-chip-${statusOption.value}`} className="filter-chip-label">
                  <i className={`fas ${isChecked ? 'fa-check-circle' : 'fa-home'} filter-chip-icon`}></i>
                  {t(statusOption.labelKey)}
                </label>
              </div>
            );
          })}
        </div>
      </div>

      {/* SEARCH FILTER */}
      <div className="card filter-card">
        <h5 className="filter-card-title">
          <i className="fas fa-search"></i> {t('adoptionPage.search_label')}
        </h5>
        <div className="search-filter-wrapper">
          <input
            type="text"
            className="search-filter-input"
            placeholder={t('adoptionPage.search_placeholder')}
            value={filterState.search || ''}
            onChange={handleSearchChange}
          />
          <span className="search-filter-icon">
            <i className="fas fa-search"></i>
          </span>
          {filterState.search && (
            <button
              type="button"
              className="search-clear-btn"
              onClick={clearSearch}
              aria-label="Clear search"
            >
              &times;
            </button>
          )}
        </div>
      </div>

      {/* CLEAR ALL BUTTON */}
      {hasActiveFilters && (
        <button
          type="button"
          className="clear-filters-btn"
          onClick={resetAllFilters}
        >
          <i className="fas fa-redo-alt"></i> {t('adoptionPage.clear_filters')}
        </button>
      )}
    </div>
  );
}
